import React, { useContext } from 'react';
import { List } from 'immutable';
import { useParams, generatePath } from 'react-router-dom';

import { hrefObjectToString } from '@/domain/router/helpers';
import ROUTES_PATH from '@/router/routesPathConfig';
import { isPaymentProcessCategory, isToApproveCategory } from '@/domain/payCategories/helpers';
import { useIntl } from 'react-intl';

import { ListItemIcon } from '@/components/mui/Layouts/components/Drawer';
import ListItemText from '@mui/material/ListItemText';
import {
  ListItem,
  ListItemButton,
  ListItemImage,
} from '@/pages/dokkaPay/pages/company/components/CompanyPayNavigation/Categories/StyledComponents';
import RouterNavLink from '@/components/mui/Router/RouterNavLink';

import { LayoutPayCompanyContext } from '@/components/mui/Layouts/PayCompany/LayoutPayCompanyContext';

import { flowStates } from '@/pages/dokkaPay/pages/company/components/PaymentsWorkspace/components/Workspace/components/PaymentFlowStates';

interface ItemProps {
  item: any; // Record, dont try destraction
  onCategoryClick: (...args: any[]) => void;
  level: number;
  tree: List<any>;
}

const Item: React.FC<ItemProps> = ({ item, onCategoryClick, level, tree }) => {
  const { formatMessage } = useIntl();
  const { toggle } = useContext(LayoutPayCompanyContext);
  const { companyId, category1 } = useParams();
  const isPendingApproval = isToApproveCategory(item);
  const isPaymentProcess = isPaymentProcessCategory(item);

  const path = generatePath(ROUTES_PATH.PAY_COMPANY.absolute, {
    companyId,
    category1: item.id,
  });

  const linkTo = hrefObjectToString({
    pathname: path,
    search: item.id ? '' : `?flowState=${flowStates.paymentPreparation}`,
  });

  const onClick = (e: React.MouseEvent) => {
    onCategoryClick(e, item);
  };

  const hasChildren = tree.size > 0;

  return (
    <>
      <ListItem sx={{ justifyContent: toggle ? 'flex-start' : 'center' }}>
        <ListItemButton
          {...({
            component: RouterNavLink,
            href: linkTo,
            activeClassName: (category1 && !isPaymentProcess) || (isPaymentProcess && !category1) ? 'Mui-selected' : '',
          } as any)}
          onClick={onClick}
          isPendingApproval={isPendingApproval && item.count > 0}
          dense
          sx={{ flexGrow: toggle ? 1 : 0 }}
        >
          {item.icon && (
            <ListItemIcon sx={{ mr: toggle ? 1 : 0 }}>
              <ListItemImage src={item.icon} />
            </ListItemIcon>
          )}
          <ListItemText
            disableTypography
            sx={{
              display: toggle ? 'flex' : 'none',
              whiteSpace: 'normal',
            }}
          >
            {formatMessage({
              id: `category.name.${item.nameId}`,
              defaultMessage: item.displayName,
            })}
            {/* payment process does not have an ID and we do not want to show a counter for it. */}
            {item.id && ` (${item.count ?? 0})`}
          </ListItemText>
        </ListItemButton>
      </ListItem>
      {hasChildren && level <= 2 && toggle && <Categories tree={tree} onCategoryClick={onCategoryClick} />}
    </>
  );
};

interface CategoriesProps {
  tree: List<any>;
  onCategoryClick: (...args: any[]) => void;
}

const Categories: React.FC<CategoriesProps> = ({ tree, onCategoryClick }) =>
  tree.map((item) => (
    <Item onCategoryClick={onCategoryClick} key={item.id} item={item} level={item.level} tree={item.children} />
  )) as unknown as React.ReactElement;

export default Categories;
